import type { FC, ReactNode } from 'react'

import { ArrowUpRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PageHero } from '@/components/generals/page-hero'
import { ButtonLink } from '@/components/generals/button'
import { CtaBand } from '@/components/generals/cta-band'
import { Eyebrow } from '@/components/generals/eyebrow'
import { SectionHeading } from '@/components/generals/section-heading'
import { TransitionLink } from '@/components/generals/transition-link'
import { Enter } from '@/components/motion/hero-title'
import { Reveal } from '@/components/motion/reveal'
import { BeforeAfter } from '@/components/widgets/before-after'
import { ProjectGrid } from '@/components/widgets/project-grid'
import { featuredProject, projects } from '@/constants/projects'
import { serviceAreas } from '@/constants/areas'
import { companyStats } from '@/constants/trust'
import { QUOTE_PATH } from '@/constants/site'

const doorsServiced = companyStats[1]

const ProjectsPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Projects'
                description="Before and after photos of garage door jobs around the Riverton metro, each with the product we used and the time we spent on site."
            />

            <PageHero
                eyebrow="Our work"
                title="Before we arrived. After we left."
                accent={['After', 'we', 'left.']}
                description="Each project shows where the door started, what went in and how long we were on site."
                actions={(
                    <>
                        <ButtonLink href='#all-projects' size='lg'>Browse all projects</ButtonLink>
                        <ButtonLink href={`${QUOTE_PATH}?service=new-doors&entry=help_me_choose`} variant='outline' size='lg'>Help me pick a door</ButtonLink>
                    </>
                )}
            >
                <Enter delay={640} className='mt-14 grid grid-cols-3 gap-px overflow-hidden rounded-[22px] border border-ink/10 bg-ink/10 sm:max-w-xl'>
                    {[
                        { value: String(projects.length), label: "Jobs featured" },
                        { value: `${doorsServiced.display}${doorsServiced.suffix ?? ''}`, label: "Doors worked on" },
                        { value: String(serviceAreas.length), label: "Towns served" }
                    ].map(stat => (
                        <div key={stat.label} className='bg-cream p-4 sm:p-5'>
                            <p className='font-heading text-[1.9rem] font-semibold leading-none tracking-[-0.04em] text-ink'>{stat.value}</p>
                            <p className='mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-slate sm:text-[11px]'>{stat.label}</p>
                        </div>
                    ))}
                </Enter>
            </PageHero>

            <section id='featured' className='relative overflow-hidden bg-ink py-20 text-cream md:py-28'>
                <div aria-hidden='true' className='door-grooves pointer-events-none absolute inset-0 opacity-25' />

                <Container className='relative'>
                    <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                        <div>
                            <Eyebrow tone='light'>In focus · {featuredProject.category}</Eyebrow>

                            <h2 className='mt-5 font-heading text-h2 font-semibold text-balance text-cream'>
                                {featuredProject.title}
                            </h2>
                        </div>

                        <p className='font-mono text-[12px] uppercase tracking-[0.14em] text-cream/50'>
                            Slide the handle to see the change
                        </p>
                    </div>

                    <Reveal className='mt-12' y={60}>
                        <figure>
                            <BeforeAfter
                                before={featuredProject.before}
                                after={featuredProject.after}
                                alt={featuredProject.alt}
                                className='aspect-[4/3] rounded-[28px] md:aspect-[16/8] md:rounded-[36px]'
                            />

                            <figcaption className='mt-8 grid gap-6 md:grid-cols-12 md:items-start'>
                                <p className='text-[16px] leading-relaxed text-cream/70 md:col-span-7'>
                                    {featuredProject.caption ?? featuredProject.description}
                                </p>

                                <div className='md:col-span-5 md:flex md:justify-end'>
                                    <TransitionLink
                                        href={`/projects/${featuredProject.slug}`}
                                        className='group inline-flex items-center gap-3 rounded-full border border-cream/20 py-2 pl-5 pr-2 text-[15px] font-medium text-cream transition-colors hover:border-cream/50'
                                    >
                                        Open the full project
                                        <span className='flex size-9 items-center justify-center rounded-full bg-ember text-white transition-transform duration-500 ease-expo group-hover:rotate-45'>
                                            <ArrowUpRight className='size-4' aria-hidden='true' />
                                        </span>
                                    </TransitionLink>
                                </div>
                            </figcaption>
                        </figure>
                    </Reveal>
                </Container>
            </section>

            <section id='all-projects' className='scroll-mt-24 py-24 md:py-32'>
                <Container>
                    <SectionHeading
                        eyebrow="The full list"
                        title="Small fixes, full replacements and everything between."
                        titleClassName='max-w-[20ch]'
                        description="Pick a project to compare the before and after, see the product we fitted and find out how long the job took."
                    />

                    <div className='mt-12'>
                        <ProjectGrid />
                    </div>
                </Container>
            </section>

            <CtaBand
                eyebrow="Your project"
                title="Seen a door you'd like on your own house?"
                body="Describe your home and we'll recommend three doors, each with an installed price."
                primary={{ label: "Suggest three doors", href: `${QUOTE_PATH}?service=new-doors&entry=help_me_choose` }}
            />
        </Layout>
    )
}

export default ProjectsPage
